import { uptimeFigure } from "./dashboard";
import type { UptimeDay } from "./types";

/**
 * Per-station uptime, as the fleet table's cell and the detail page's chart read
 * it (`routes/chargePoints.ts` — `/charge-points` rows and `/:id/uptime`).
 */

/**
 * Below this a figure is drawn in the fault colour. 95% is about eight hours
 * offline in a week: long enough that someone should look at the station, and
 * past the noise of a reboot or a deploy.
 */
export const UPTIME_ATTENTION_BELOW = 0.95;

export function isUptimeLow(ratio: number | null): boolean {
  return ratio !== null && ratio < UPTIME_ATTENTION_BELOW;
}

const dayFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

/**
 * A `YYYY-MM-DD` the server already resolved in the depot's zone. Read as UTC
 * midnight and printed at UTC, so the browser's own zone cannot move it a day.
 */
export function dayLabel(date: string): string {
  return dayFormat.format(new Date(`${date}T00:00:00Z`));
}

export function hoursLabel(seconds: number): string {
  if (seconds < 3_600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3_600).toFixed(1)} h`;
}

/** One day as a sentence: the bar's tooltip and its accessible name. */
export function describeUptimeDay(day: UptimeDay): string {
  const label = dayLabel(day.date);
  if (day.ratio === null) return `${label} · no history`;
  const drops = `${day.disconnects} ${day.disconnects === 1 ? "disconnect" : "disconnects"}`;
  return `${label} · ${uptimeFigure(day.ratio)}% · ${hoursLabel(day.connectedSeconds)} connected · ${drops}`;
}
